import {useEffect, useState} from "react";
import {Copy, KeyRound, ShieldAlert} from "lucide-react";
import {PhoneHQButton, PhoneHQCheckbox, PhoneHQDialog, PhoneHQDialogFooter, PhoneHQInput} from "ch3-ui-lib";
import {
    ApiKeySummary,
    IntegrationStatusEnum,
    useCreateApiKeyMutation,
} from "../../../store/features/integrations/integrationsService";

interface Props {
    open: boolean;
    suggestedName?: string;
    onOpenChange: (open: boolean) => void;
    onStored?: (key: ApiKeySummary) => void;
}

export const ApiKeyCreationDialog = ({open, suggestedName = '', onOpenChange, onStored}: Props) => {
    const [name, setName] = useState(suggestedName);
    const [created, setCreated] = useState<({summary: ApiKeySummary; secret: string}) | null>(null);
    const [copied, setCopied] = useState(false);
    const [storedConfirmed, setStoredConfirmed] = useState(false);
    const [copyError, setCopyError] = useState<string | null>(null);
    const [createError, setCreateError] = useState<string | null>(null);
    const [confirmDiscard, setConfirmDiscard] = useState(false);
    const [createApiKey, {isLoading, reset: resetCreateApiKey}] = useCreateApiKeyMutation();

    useEffect(() => {
        if (open && !created) setName(suggestedName);
    }, [created, open, suggestedName]);

    const reset = () => {
        setName(suggestedName);
        setCreated(null);
        setCopied(false);
        setStoredConfirmed(false);
        setCopyError(null);
        setCreateError(null);
        setConfirmDiscard(false);
        resetCreateApiKey();
    };

    const close = () => {
        reset();
        onOpenChange(false);
    };

    const requestClose = () => {
        if (created && (!copied || !storedConfirmed)) {
            setConfirmDiscard(true);
            return;
        }
        close();
    };

    const create = async () => {
        if (!name.trim()) return;
        setCreateError(null);
        try {
            const response = await createApiKey({
                data: {name: name.trim(), status: IntegrationStatusEnum.ENABLED},
            }).unwrap();
            setCreated({
                summary: {
                    id: response.id,
                    name: response.name,
                    status: response.status,
                    apiKeyShortcut: response.apiKeyShortcut,
                },
                secret: response.apiKey,
            });
        } catch {
            setCreateError('API key creation failed. Try again.');
        }
    };

    const copySecret = async () => {
        if (!created) return;
        setCopyError(null);
        try {
            await navigator.clipboard.writeText(created.secret);
            setCopied(true);
        } catch {
            setCopyError('Clipboard access failed. Check browser permissions and try again.');
        }
    };

    const finish = () => {
        if (!created || !copied || !storedConfirmed) return;
        onStored?.(created.summary);
        close();
    };

    return (
        <PhoneHQDialog
            open={open}
            onOpenChange={(nextOpen) => nextOpen ? onOpenChange(true) : requestClose()}
            title={confirmDiscard ? 'Discard the one-time secret?' : 'Create API key'}
            theme="light"
        >
            {confirmDiscard && created ? (
                <div className="flex flex-col gap-4">
                    <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm leading-5 text-amber-900">
                        <ShieldAlert size={20} className="mt-0.5 shrink-0"/>
                        <div>
                            PHQ cannot display this secret again. If you leave without storing it, create a replacement and disable or delete <span className="font-semibold">{created.summary.name}</span> in Admin → Integrations → API.
                        </div>
                    </div>
                    <PhoneHQDialogFooter>
                        <PhoneHQButton variant="secondary" onClick={() => setConfirmDiscard(false)}>Keep secret open</PhoneHQButton>
                        <PhoneHQButton variant="primary" style={{background: '#dc2626', borderColor: '#dc2626'}} onClick={close}>
                            Discard and close
                        </PhoneHQButton>
                    </PhoneHQDialogFooter>
                </div>
            ) : !created ? (
                <div className="flex flex-col gap-4">
                    <div className="flex items-start gap-3 rounded-lg border border-line bg-surface-muted p-3 text-sm leading-5 text-ink-secondary">
                        <KeyRound size={19} className="mt-0.5 shrink-0 text-brand-ink"/>
                        Use one key per external integration and environment. The name is descriptive metadata, not a permission boundary.
                    </div>
                    <PhoneHQInput
                        id="apiKeyCreation-name"
                        label="Key name"
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        placeholder="ENS / Datadog / production"
                    />
                    {createError && <div className="text-sm text-danger-ink">{createError}</div>}
                    <PhoneHQDialogFooter>
                        <PhoneHQButton variant="secondary" onClick={requestClose}>Cancel</PhoneHQButton>
                        <PhoneHQButton variant="primary" disabled={!name.trim() || isLoading} onClick={create}>
                            {isLoading ? 'Creating…' : 'Create API key'}
                        </PhoneHQButton>
                    </PhoneHQDialogFooter>
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm leading-5 text-amber-900">
                        <ShieldAlert size={20} className="mt-0.5 shrink-0"/>
                        This is the only time PHQ will display the full secret. Copy it into the external system's secret manager before closing.
                    </div>
                    <PhoneHQInput
                        id="apiKeyCreation-secret"
                        label="One-time API key secret"
                        value={created.secret}
                        readOnly
                        className="font-mono"
                    />
                    <PhoneHQButton variant="secondary" onClick={copySecret}>
                        <Copy size={16}/>
                        {copied ? 'Copied' : 'Copy API key'}
                    </PhoneHQButton>
                    {copyError && <div className="text-sm text-danger-ink">{copyError}</div>}
                    <div className={!copied ? 'pointer-events-none opacity-50' : ''}>
                        <PhoneHQCheckbox
                            checked={storedConfirmed}
                            disabled={!copied}
                            onCheckedChange={(checked) => setStoredConfirmed(checked === true)}
                            label="I stored this secret in an external secret manager"
                        />
                    </div>
                    <PhoneHQDialogFooter>
                        <PhoneHQButton variant="secondary" onClick={requestClose}>Close</PhoneHQButton>
                        <PhoneHQButton variant="primary" disabled={!copied || !storedConfirmed} onClick={finish}>
                            Continue
                        </PhoneHQButton>
                    </PhoneHQDialogFooter>
                </div>
            )}
        </PhoneHQDialog>
    );
};
